import { MetadataProps } from '@/features/SEO/SEO.interface';
import {
  backgroundColorMap,
  CMS_BASE_URL,
  ColorNames,
  contentToCategoryMap,
  opacityLevelsMap,
  spacingSizesMap,
  SPA_BASE_URL,
  SSO_BASE_URL,
  textColorMap,
  Theme,
} from '@/utils/constants';
import { isEmpty, isString } from 'lodash';
import md5 from 'md5';
import qs from 'query-string';
import React, { ReactElement } from 'react';
import ReactDOMServer from 'react-dom/server';
import TagManager from 'react-gtm-module';
import { Link as RouterLink } from 'react-router-dom';
import urllite from 'urllite/lib/core';
import NextId from './nextId';

export const isTelephoneLink = (link: string) => /^tel:/.test(link);
const LANG_PATH_PATTERN = /\/[a-z]{2}$/;

export const reduceText = (text: string, limit = 200) =>
  text.length > limit ? `${text.substring(0, limit)}...` : text;

export const titleToSnake = (key: string) => key.split(/ |-/g).join('_').toLowerCase();

const entityMap: any = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;',
};

export function escapeHtml(value: string) {
  return String(value).replace(/[&<>"'`=\/]/g, function (s) {
    return entityMap[s];
  });
}

export const formatText = (text: string, values: string[]) => {
  return text.replace(/%(\d+)/g, (_, m) => values[m - 1]);
};

export const snakeCaseToTitle = (key: string) =>
  key
    .split('_')
    .map((text, index) => (index ? text : text.slice(0, 1).toUpperCase() + text.slice(1)))
    .join(' ');

export const encodeSvg = (reactElement: ReactElement<any>) => {
  return `data:image/svg+xml,${escape(ReactDOMServer.renderToStaticMarkup(reactElement))}`;
};

export const em = ({ size = 16, base = 16 }) => {
  return `${size / base}em`;
};

export const hexToRGBA = (hex: string, alpha = 1) => {
  const hexcode = hex.length === 4 ? hex + hex.slice(1, 4) : hex;
  const [r, g, b] = hexcode.match(/\w\w/g)?.map((x) => parseInt(x, 16)) || [];

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const matchExternalLink = (url: string) => /^https?:\/\//.test(url);

const internalSubdomains = new Set([
  'localhost',
  'genetec.com',
  'www.genetec.com',
  'resources.genetec.com',
  'recursos.genetec.com',
  'ressources.genetec.com',
  'info.genetec.com',
  'experiences.genetec.com',
  'bloomreachsite-dev.genetec.com',
  'bloomreachsite-qa.genetec.com',
  'bloomreachsite-uat.genetec.com',
]);
export const isExternalLinkContent = (url: string) => {
  if (!/^https?:\/\//.test(url)) return false;

  const link = new URL(url);
  return !internalSubdomains.has(link.hostname);
};

export const isEmailLink = (link?: string | null) => {
  if (!link) return false;
  return /^mailto:/.test(link);
};

export const isInternalEmailLink = (link = '') => {
  const splitted = link?.split('@');
  return internalSubdomains.has(splitted[splitted.length - 1] || '');
};

interface ThemeColors {
  theme: ThemeType;
  baseColor?: keyof (typeof textColorMap)[Theme.light];
  opacityLevel?: keyof (typeof opacityLevelsMap)[Theme.light];
}
export const mapThemeToTextColor = ({
  theme = Theme.light,
  baseColor = 'default',
  opacityLevel = 0,
}: ThemeColors) => {
  const textColor = textColorMap[theme][baseColor];
  const alpha = opacityLevelsMap[theme][opacityLevel];

  return hexToRGBA(textColor, alpha);
};

export const mapColorNameToTheme = (colorName = '') => {
  switch (colorName) {
    case ColorNames.white:
    case ColorNames.eggshell:
      return Theme.light;
    case ColorNames.mediumGray:
    case ColorNames.charcoal:
    case ColorNames.darkGray:
    case ColorNames.black:
      return Theme.dark;
    default:
      return Theme.dark;
  }
};

export const mapColorNameToContrastColorName = ({ colorName = '', darker = false }: any) => {
  switch (colorName) {
    case ColorNames.white:
      return ColorNames.eggshell;
    case ColorNames.eggshell:
      return ColorNames.white;
    case ColorNames.mediumGray:
      return ColorNames.charcoal;
    case ColorNames.charcoal:
      return darker ? ColorNames.darkGray : ColorNames.mediumGray;
    case ColorNames.darkGray:
      return darker ? ColorNames.black : ColorNames.charcoal;
    case ColorNames.black:
      return ColorNames.darkGray;
    default:
      return ColorNames.eggshell;
  }
};

export const mapAlignmentToFlex = (alignment = '') => {
  if (/^center$/i.test(alignment)) {
    return 'center';
  }
  if (/^right$/i.test(alignment)) {
    return 'flex-end';
  }

  return 'flex-start';
};

interface ThemeBackground {
  theme: ThemeType;
  baseColor?: keyof (typeof backgroundColorMap)[Theme.light];
}
export const mapThemeToBgColor = ({
  theme = Theme.light,
  baseColor = 'default',
}: ThemeBackground) => backgroundColorMap[theme][baseColor];

export const mapPixelsToInt = (px?: string) => (px ? parseInt(px, 10) : 0);

const scrollTo = (identifier: string) => {
  const anchor = document.querySelector(`#${identifier}`) as Element;
  anchor?.scrollIntoView({
    behavior: 'smooth',
  });
};

export const getSafeURI = (rawText: string) => {
  try {
    return encodeURI(decodeURI(rawText));
  } catch (e) {
    console.warn(rawText, e);
    return '/';
  }
};

export const jumpLinkScrollTo = ({ event, href = '' }: { event: any; href: string }) => {
  const identifier = href.replace(/^#/, '')?.trim();
  if (!identifier) return;
  scrollTo(identifier);

  if (!identifier.match(/-form$/)) return;
  const formId = document
    ?.querySelector(`#${identifier}`)
    // withGTM wraps elements adding extra "div" elements, this time is needed go up 2 levels.
    ?.parentNode?.parentNode?.querySelector('form[data-form]')
    ?.getAttribute('data-form');
  const formInstance = window?.MktoForms2?.allForms()?.find(
    (form: any) => form?.getId() === Number(formId),
  );
  const formValues = formInstance?.getValues();
  // If the element to scroll to is a form, then we want to track the click.
  const target = (event?.target as HTMLElement)?.closest('a,button');
  const position =
    target?.closest('[data-datalayer-position]')?.getAttribute('data-datalayer-position') || '';
  const isTargetMuiButton = target?.classList.contains('MuiButton-root');

  const dataLayer = {
    event: 'trackEvent-ctaClickToForm',
    event_name: 'cta_click_to_form',
    ...(!!position && { position }),
    type: isTargetMuiButton ? 'button_cta_scroll_to_form' : 'link_cta_scroll_to_form',
    link_text: target?.textContent?.trim(),
    ...(!!formInstance && { form_type: formValues?.formType }),
    ...(!!formInstance && { form_name: formValues?.formName }),
    ...(!!formInstance && { form_id: formId }),
  };
  TagManager.dataLayer({ dataLayer });
};

export const mapLinkToProps = (link?: LinkType) => {
  if (!link || !link.href) return {};

  const { href, openInNewWindow } = link;
  const isExternalLink =
    (href && matchExternalLink(href)) ||
    openInNewWindow ||
    isEmailLink(href) ||
    isTelephoneLink(href);
  const isJumplink = /^#/.test(href);

  let component: string | any = RouterLink;
  if (isJumplink) component = 'button';
  if (isExternalLink) component = 'a';

  const props: any = { component };
  const jumplinkProps = isJumplink
    ? {
        'data-anchor-id': isJumplink ? href : undefined,
        // If it's a "link_to_scroll" then it's always a button.
        'data-datalayer-cta-type': 'link_to_scroll',
        onClick: (event: any) => {
          event.preventDefault();
          jumpLinkScrollTo({ event, href });
        },
      }
    : {};

  if (isJumplink) return { ...props, ...jumplinkProps };

  if (isExternalLink)
    return { ...props, ...jumplinkProps, href, target: openInNewWindow ? '_blank' : undefined };

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return { ...props, to: getSafeURI(href) };
};

export const formatDate = (time: number) =>
  new Date(time).toLocaleDateString('en-US', { year: 'numeric', day: 'numeric', month: 'long' });

const slugifyDefault = (label: string) =>
  label
    .toLowerCase()
    .replace(/^\s+|\s+$/g, '')
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/\?+/g, '-');

const slugifyCustom = ({ label = '', rules = '' }) =>
  decodeURI(label)
    .toLowerCase()
    .replace(new RegExp(rules, 'gi'), '')
    .replace(/#+/g, '')
    .replace(/^\s+|\s+$/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/'+/g, '-')
    .replace(/,+/g, '-')
    .replace(/\?+/g, '-');

interface SlugifyProps {
  label: string;
  rules?: string;
}

export const slugify = ({ label, rules = '' }: SlugifyProps) => {
  if (!rules.trim().length) return slugifyDefault(label);

  return slugifyCustom({ label, rules });
};

/**
 * Find Greatest common divisor
 */
export const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

export const triggerEvent = ({
  target,
  eventName,
  eventDetail,
}: {
  target: Element | Document;
  eventName: string;
  eventDetail?: any;
}) => {
  const event = new CustomEvent(eventName, { detail: eventDetail, bubbles: true });
  target.dispatchEvent(event);
};

export const getAttributeNames = ({ target }: { target: HTMLElement }) => {
  if (target.getAttributeNames) {
    return target.getAttributeNames();
  }

  const { attributes = [] } = target || {};
  const { length = 0 } = attributes;

  return new Array(length).fill(null).map((_, i) => attributes[i].name);
};

/**
 * Generates an unique hash string of an object with certain keys.
 */
export const hashObject = ({ content, keys }: { content: any; keys: any[] }): string => {
  const hashableContent = keys.reduce(
    (prev: any, curr: any) => ({ ...prev, [curr]: content[curr] }),
    {},
  );

  return md5(JSON.stringify(hashableContent));
};

export const stringifyParams = (params: any) => {
  if (isString(params)) return params;

  try {
    return JSON.stringify(params);
  } catch (ex) {
    return '';
  }
};

/**
 * Returns the URL to redirect to after login.
 * If the CMS base URL is different from the SSO base URL, then the CMS base URL is used.
 * Otherwise, the SPA base URL is removed from the current URL.
 * If the URL is empty, then the current URL is used.
 *
 * @param url The URL to redirect to after login.
 * @param cmsBaseUrl The CMS base URL.
 * @param spaBaseUrl The SPA base URL.
 * @param ssoBaseUrl The SSO base URL.
 * @returns The URL to redirect to after login.
 */
export function getSSOLocationURL({
  url,
  cmsBaseUrl = CMS_BASE_URL,
  spaBaseUrl = SPA_BASE_URL,
  ssoBaseUrl = SSO_BASE_URL,
}: {
  url?: string;
  cmsBaseUrl?: string;
  spaBaseUrl?: string;
  ssoBaseUrl?: string;
} = {}) {
  if (cmsBaseUrl !== ssoBaseUrl) {
    return url ? `${cmsBaseUrl}${url}` : window.location.href;
  }

  return url ?? `${window.location.pathname}${window.location.search}`;
}

/**
 * Checks if a path is absolute (starts with a protocol like http://, https://, etc)
 * @param path The path to check
 * @returns True if the path is absolute, false otherwise
 */
export const isPathAbsolute = (path: string) => /^(?:[a-z]+:\/\/)/.test(path);

export const getPageUrl = ({ url, page }: { url: string | undefined; page: Br.Page }) => {
  if (!url) return url;

  const desireUrl = urllite(url);
  const SPA_BASE_URL_REGEX = new RegExp(`^${SPA_BASE_URL?.replace(/\/$/, '')}`);
  const desireUrlPathname = desireUrl.pathname.replace(SPA_BASE_URL_REGEX, '').replace(/\/$/, '');
  // If it's an absolute path returns as it comes.
  if (isPathAbsolute(url) || isEmailLink(url) || isTelephoneLink(url)) {
    return url;
  }

  const fullUrlPath =
    desireUrlPathname +
    (desireUrl.search.length > 1 ? desireUrl.search : '') +
    (desireUrl.hash.length ? desireUrl.hash : '');

  return page?.getUrl(fullUrlPath);
};

export const isAnchorSectionLink = (hrefText: any) => {
  return /^#.*/.test(hrefText);
};

export const replaceLinks = ({ content = '', page }: { content: string; page?: Br.Page }) => {
  if (!page) return content;

  const replaceHrefLink = (fullMatch: string, match: string) => {
    if (isAnchorSectionLink(match)) {
      return `${fullMatch} data-anchor-id=${match}`;
    }
    return fullMatch.replace(match, getPageUrl({ url: match, page }) || '');
  };

  return content.replace(/(?:href=")(.*?)(?:")/g, replaceHrefLink);
};

export const getClassSelector = (className: string) => {
  return className ? className.split(' ')[0] : className;
};

export const getNextId = (localPrefix = 'id') => {
  // eslint-disable-next-line no-plusplus
  return `${localPrefix}${NextId.getInstance().lastId++}`;
};

export const filterObj = (obj: any, fun: any) =>
  Object.entries(obj).reduce(
    (prev, [key, value]) => ({
      ...prev,
      ...(fun(key, value) ? { [key]: value } : {}),
    }),
    {},
  );

export const mapContentTypeToCategoryType = (contentType = '') =>
  contentToCategoryMap.get(contentType.toLowerCase()) || '';

export const mapDocumentUrlToCategoryType = ({ url }: { url: string }) => {
  if (/\/customer-stories/.test(url)) {
    return mapContentTypeToCategoryType('customer story');
  }

  if (/\/press-releases/.test(url)) {
    return mapContentTypeToCategoryType('press release');
  }

  if (/\/news-coverage/.test(url)) {
    return mapContentTypeToCategoryType('news coverage');
  }

  if (/\/blog/.test(url)) {
    return mapContentTypeToCategoryType('blog');
  }

  if (/\/podcast/.test(url)) {
    return mapContentTypeToCategoryType('podcast');
  }

  if (/\/legal/.test(url)) {
    return mapContentTypeToCategoryType('legal');
  }

  // adding product-release with translation to cover all languages match
  if (
    /\/product-releases|produkt-releases|lanzamientos-de-productos|lancements-de-produits|release-prodotti|productlanceringen|lancamentos-de-produtos/.test(
      url,
    )
  ) {
    return mapContentTypeToCategoryType('product releases');
  }

  return undefined;
};

export const capitalize = (s: string) => {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const formatTagText = (tagText: string) => {
  if (typeof tagText !== 'string') return '';
  return capitalize(tagText.replace(/-/g, ' '));
};

export const getTagLabel = (tag: any) => {
  const path = tag && tag.length > 0 ? tag.split(/\//s) : null;
  return path && path.length > 0 ? formatTagText(path.pop()) : null;
};

export const getChipCategoryLabel =
  ({ isCategory, labels = {} }: { isCategory: boolean; labels: any }) =>
  (item: string): string => {
    if (isCategory) return item;

    return labels?.[item] || item;
  };

const SPACING_SMALL = {
  mobile: spacingSizesMap.small * 0.7,
  desktop: spacingSizesMap.small,
};

const SPACING_MEDIUM = {
  mobile: spacingSizesMap.medium * 0.7,
  desktop: spacingSizesMap.medium,
};

const SPACING_LARGE = {
  mobile: spacingSizesMap.large * 0.7,
  desktop: spacingSizesMap.large,
};

export const getVerticalSpacing = (muiTheme: any, classname?: string) => ({
  [classname as string]: {
    [muiTheme.breakpoints.down('sm')]: {
      '&.top-small': {
        paddingTop: SPACING_SMALL.mobile,
      },

      // Default
      '&.top-medium': {
        paddingTop: SPACING_MEDIUM.mobile,
      },

      '&.top-large': {
        paddingTop: SPACING_LARGE.mobile,
      },

      '&.bottom-small': {
        paddingBottom: SPACING_SMALL.mobile,
      },

      // Default
      '&.bottom-medium': {
        paddingBottom: SPACING_MEDIUM.mobile,
      },
    },

    '&.bottom-large': {
      paddingBottom: SPACING_LARGE.mobile,
    },

    [muiTheme.breakpoints.up('md')]: {
      '&.top-small-md': {
        paddingTop: SPACING_SMALL.desktop,
      },

      '&.top-medium-md': {
        [muiTheme.breakpoints.up('md')]: {
          paddingTop: SPACING_MEDIUM.desktop,
        },
      },

      '&.top-large-md': {
        '& $content, $topBackground': {
          [muiTheme.breakpoints.up('md')]: {
            paddingTop: SPACING_LARGE.desktop,
          },
        },
      },

      '&.bottom-small-md': {
        [muiTheme.breakpoints.up('md')]: {
          paddingBottom: SPACING_SMALL.desktop,
        },
      },

      '&.bottom-medium-md': {
        [muiTheme.breakpoints.up('md')]: {
          paddingBottom: SPACING_MEDIUM.desktop,
        },
      },

      '&.bottom-large-md': {
        [muiTheme.breakpoints.up('md')]: {
          paddingBottom: SPACING_LARGE.desktop,
        },
      },
    },
  },
});

export const parseHTMLString = (htmlString?: string) => {
  if (!htmlString) return '';

  try {
    return new DOMParser()
      .parseFromString(htmlString, 'text/html')
      .body.innerText.replace(/\n\n/g, '. ')
      .replace(/\.\./g, '.');
  } catch (e) {
    return '';
  }
};

export const stringToHTML = (htmlString: string) => {
  try {
    return new DOMParser().parseFromString(htmlString, 'text/html');
  } catch (e) {
    return null;
  }
};

export const htmlToReact = (element: HTMLElement, indexKey?: number): ReactElement => {
  const { localName, attributes, childNodes } = element;
  const key = indexKey !== undefined ? { key: indexKey } : {};

  const attrs: { [key: string]: string } = {};

  if (element.hasAttributes()) {
    // eslint-disable-next-line no-restricted-syntax
    for (const attr of attributes) {
      attrs[attr.name] = attr.value;
    }
  }

  const children = Array.from(childNodes)
    .map((node) => {
      if (node.nodeType === 3) return node.textContent;
      if (node.nodeType === 1) return htmlToReact(node as HTMLElement);
      return null;
    })
    .filter(Boolean);
  const reactElement = React.createElement(
    localName,
    { ...attrs, ...key },
    children.length ? children : null,
  );
  return reactElement;
};

export const stringToReact = (htmlString: string, target: 'body' | 'head') => {
  const parsed = stringToHTML(htmlString);
  if (!parsed) return null;

  const children = Array.from(parsed[target].children);

  return children.map((child, index) => htmlToReact(child as HTMLElement, index));
};

export const readCsvLine = ({ data = '', separator = ',' }): string[] => {
  if (!data.trim().length) return [];

  return data.split(separator).map((item: string) => item.trim());
};

export const setPageLink = (pageNum: number) => {
  const { pathname: url, search } = window.location;
  const { page, ...query } = qs.parse(search);
  if (pageNum > 1) query.page = `${pageNum}`;

  return qs.stringifyUrl({ url, query });
};

export const isPrerender = (): boolean => /prerender/i.test(window.navigator.userAgent);

const parts = ['prerender', 'bot', 'crawler', 'spider', 'bing', 'facebook'];
export const isBot = () => parts.some((part) => navigator.userAgent.toLowerCase().includes(part));

interface DocumentBasedImageProps {
  pageMetadata?: MetadataProps;
  cardImage?: ImageType;
}
export const getDocumentBasedImage = (props: DocumentBasedImageProps) => {
  const { pageMetadata, cardImage = {} } = props;

  if (!isEmpty(pageMetadata?.socialMediaImage)) return pageMetadata?.socialMediaImage;

  return cardImage;
};

const isSVG = (rawImage: ImageType): boolean => {
  const image = rawImage?.original?.url || '';

  return /.svg$/.test(image);
};

export const getImageUrl = (rawImage: ImageType): string =>
  (isSVG(rawImage) ? rawImage?.original?.url : rawImage?.small?.url) || '';

/**
 * Video should NOT be autoplayed if it's seen in a iOS linkedin browser.
 * It fullscreens on that browser variant.
 */
export const isAutoplayVideoAllowed = !/linkedinapp/i.test(window.navigator.userAgent);

export const elasticFilters = () => {
  const { q, keywords, page, sortBy, size, ...filters } = qs.parse(window.location.search);

  return filters;
};

export const getAlternateBaseURL = (language = 'en') => {
  const baseURL = CMS_BASE_URL;
  const thisPath = LANG_PATH_PATTERN.exec(baseURL)?.[0] || '';
  const newPath = language && language !== 'en' ? `/${language}` : '';
  const altBaseURL = Boolean(thisPath !== newPath) && baseURL.replace(LANG_PATH_PATTERN, newPath);

  return altBaseURL || baseURL;
};

export const getResultLanguageURI = (result: any, pageLang = '') => {
  const item = result?.language?.raw || 'en';
  const page = pageLang || 'en';
  const uri = result?.url?.raw || '/';

  // if the result item's language is different, use fully-qualified URL for external link behavior
  if (item !== page && !isPathAbsolute(uri)) return `${getAlternateBaseURL(item)}${uri}`;
  return uri;
};

export const isURLWithFile = (href = '') =>
  /\.(pdf|txt|csv|doc|docx|ppt|pptx|pps|jpg|png|xls|xlsx|ods|7z|zip|rar|xml|svg|psd|avi|flv|mp4|mov|mkv|mp3)$/i.test(
    href,
  );

export const getGenetecPostMessage =
  ({ source = '', type = '' }) =>
  ({ data: dataRaw }: MessageEvent) => {
    if (!dataRaw) return undefined;
    if (!isString(dataRaw)) return undefined;

    try {
      const data = JSON.parse(dataRaw);

      if (type) {
        if (!data?.source || !data?.type || !data?.payload) return undefined;
        if (!new RegExp(source, 'i').test(data?.source)) return undefined;
        // if (!/genetec/i.test(data?.source)) return undefined;
        // If "type" is defined then it evaluates if that type message matches.
        if (!!type && data.type !== type) return undefined;

        return data.payload;
      }

      return dataRaw;
    } catch (e) {
      return undefined;
    }
  };

// source: https://emailregex.com
export const isEmail = (email: string) =>
  // eslint-disable-next-line no-useless-escape
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email,
  );

export const parseBooleanStringToBoolean = (value: string) => value === 'true';

export const isLastPageViewSamePath = (): boolean => {
  if (!window?.dataLayer) return false;

  const lastPageViewEvent = window.dataLayer.findLast(
    (event: any) => event?.event === 'gaCustomEventAsync_PageView',
  );
  if (!lastPageViewEvent) return false;

  try {
    const lastPathName = new URL(window.location.origin + lastPageViewEvent?.page_path).pathname;
    return lastPathName === window.location.pathname;
  } catch (error) {
    return false;
  }
};
